import { useState, useCallback, useEffect } from 'react';

export interface TableSelectionState {
  selectedIds: Set<string>;
  selectedCount: number;
  isSelected: (id: string) => boolean;
  toggleOne: (id: string) => void;
  toggleAll: (items: { id: string }[]) => void;
  clearSelection: () => void;
  setSelectedIds: (ids: Set<string>) => void;
  isAllSelected: (items: { id: string }[]) => boolean;
  isIndeterminate: (items: { id: string }[]) => boolean;
  selectedItems: <T extends { id: string }>(items: T[]) => T[];
}

export function useTableSelection(resetKey?: unknown): TableSelectionState {
  const [selectedIds, setSelectedIdsInternal] = useState<Set<string>>(new Set());

  // Clear selection when resetKey changes (e.g. page, filter change)
  useEffect(() => {
    setSelectedIdsInternal(new Set());
  }, [resetKey]);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const toggleOne = useCallback((id: string) => {
    setSelectedIdsInternal(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((items: { id: string }[]) => {
    setSelectedIdsInternal(prev => {
      const allSelected = items.every(i => prev.has(i.id));
      if (allSelected) {
        // deselect all
        const next = new Set(prev);
        items.forEach(i => next.delete(i.id));
        return next;
      } else {
        // select all
        const next = new Set(prev);
        items.forEach(i => next.add(i.id));
        return next;
      }
    });
  }, []);

  const setSelectedIds = useCallback((ids: Set<string>) => {
    setSelectedIdsInternal(ids);
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const isAllSelected = useCallback(
    (items: { id: string }[]) => items.length > 0 && items.every(i => selectedIds.has(i.id)),
    [selectedIds],
  );

  const isIndeterminate = useCallback(
    (items: { id: string }[]) => items.some(i => selectedIds.has(i.id)) && !items.every(i => selectedIds.has(i.id)),
    [selectedIds],
  );

  const selectedItems = useCallback(
    <T extends { id: string }>(items: T[]): T[] => items.filter(i => selectedIds.has(i.id)),
    [selectedIds],
  );

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    isSelected,
    toggleOne,
    toggleAll,
    clearSelection,
    setSelectedIds,
    isAllSelected,
    isIndeterminate,
    selectedItems,
  };
}
